import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	otherPlayer,
	type PlayerIndex,
	reject,
} from "../types";

export const CHECKERS_SIZE = 8;
/** Moves without a capture before the game is called a draw. */
export const STALL_LIMIT = 60;

export interface Piece {
	owner: PlayerIndex;
	king: boolean;
}

export type CheckersCell = Piece | null;

export interface CheckersState extends BaseState {
	kind: "checkers";
	board: CheckersCell[];
	/** Set mid-multi-jump: this piece must keep jumping. */
	mustJumpFrom: number | null;
	/** Moves since anything was captured — two kings can shuffle forever. */
	sinceCapture: number;
	lastMove: { from: number; to: number; captured: number | null } | null;
}

export interface CheckersMove {
	from: number;
	to: number;
}

const at = (col: number, row: number): number => row * CHECKERS_SIZE + col;
const colOf = (square: number): number => square % CHECKERS_SIZE;
const rowOf = (square: number): number => Math.floor(square / CHECKERS_SIZE);

/** Player 0 climbs up the board, player 1 comes down it. */
function directions(piece: Piece): number[] {
	if (piece.king) return [-1, 1];
	return piece.owner === 0 ? [-1] : [1];
}

interface Step {
	move: CheckersMove;
	captured: number | null;
}

/** Every step this piece could take, jumps and plain slides alike. */
function stepsFrom(board: CheckersCell[], square: number): Step[] {
	const piece = board[square];
	if (!piece) return [];
	const col = colOf(square);
	const row = rowOf(square);
	const steps: Step[] = [];

	for (const dr of directions(piece)) {
		for (const dc of [-1, 1]) {
			const nc = col + dc;
			const nr = row + dr;
			if (nc < 0 || nc >= CHECKERS_SIZE || nr < 0 || nr >= CHECKERS_SIZE) continue;
			const target = board[at(nc, nr)];

			if (target === null) {
				steps.push({ move: { from: square, to: at(nc, nr) }, captured: null });
				continue;
			}
			if (target.owner === piece.owner) continue;

			// Something of theirs: can we hop it?
			const jc = nc + dc;
			const jr = nr + dr;
			if (jc < 0 || jc >= CHECKERS_SIZE || jr < 0 || jr >= CHECKERS_SIZE) continue;
			if (board[at(jc, jr)] === null) {
				steps.push({ move: { from: square, to: at(jc, jr) }, captured: at(nc, nr) });
			}
		}
	}
	return steps;
}

function allSteps(board: CheckersCell[], player: PlayerIndex): Step[] {
	const steps: Step[] = [];
	board.forEach((cell, square) => {
		if (cell?.owner === player) steps.push(...stepsFrom(board, square));
	});
	return steps;
}

function countPieces(board: CheckersCell[]): [number, number] {
	let first = 0;
	let second = 0;
	for (const cell of board) {
		if (cell?.owner === 0) first++;
		else if (cell?.owner === 1) second++;
	}
	return [first, second];
}

export const checkers: GameDefinition<CheckersState, CheckersMove> = {
	id: "checkers",
	category: "classics",
	title: "Checkers",
	emoji: "🔵",
	blurb: "Hop over your friend's pieces to capture them. Reach the far side to be crowned.",
	howTo: [
		"Move one square diagonally forwards.",
		"Jump over one of their pieces to capture it — and jump again if you can.",
		"If a jump is there, you have to take it.",
		"Reach the far row and your piece is crowned, so it can go backwards too.",
		"Take all their pieces, or leave them with no move, to win.",
	],

	create() {
		const board = Array<CheckersCell>(CHECKERS_SIZE * CHECKERS_SIZE).fill(null);
		for (let row = 0; row < CHECKERS_SIZE; row++) {
			for (let col = 0; col < CHECKERS_SIZE; col++) {
				// Only the dark squares are used.
				if ((row + col) % 2 === 0) continue;
				if (row < 3) board[at(col, row)] = { owner: 1, king: false };
				if (row > CHECKERS_SIZE - 4) board[at(col, row)] = { owner: 0, king: false };
			}
		}
		return {
			kind: "checkers",
			board,
			mustJumpFrom: null,
			sinceCapture: 0,
			lastMove: null,
			turn: 0,
			scores: countPieces(board),
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const { from, to } = raw as { from?: unknown; to?: unknown };
		const cells = CHECKERS_SIZE * CHECKERS_SIZE;
		if (typeof from !== "number" || !Number.isInteger(from) || from < 0 || from >= cells)
			return null;
		if (typeof to !== "number" || !Number.isInteger(to) || to < 0 || to >= cells) return null;
		return { from, to };
	},

	apply(state, player, move): MoveOutcome<CheckersState> {
		if (state.result) return reject("This game is already finished.");
		if (state.turn !== player) return reject("It's not your turn yet.");

		const piece = state.board[move.from];
		if (!piece || piece.owner !== player) return reject("That isn't one of your pieces.");
		if (state.mustJumpFrom !== null && state.mustJumpFrom !== move.from) {
			return reject("Keep jumping with the same piece!");
		}

		const step = stepsFrom(state.board, move.from).find((entry) => entry.move.to === move.to);
		if (!step) return reject("That piece can't go there.");
		// Mid-chain, only another jump will do.
		if (state.mustJumpFrom !== null && step.captured === null) {
			return reject("You have to keep jumping.");
		}
		// Captures are forced — the rule that gives checkers its tactics. A
		// player who could decline every trade could stall a lost game forever.
		if (step.captured === null && allSteps(state.board, player).some((s) => s.captured !== null)) {
			return reject("You have to jump when you can!");
		}

		const board = [...state.board];
		board[move.from] = null;
		if (step.captured !== null) board[step.captured] = null;

		// Reaching the far row is a crowning.
		const lastRow = player === 0 ? 0 : CHECKERS_SIZE - 1;
		const king = piece.king || rowOf(move.to) === lastRow;
		board[move.to] = { owner: player, king };

		// A jump that can be followed by another keeps the turn.
		const chaining =
			step.captured !== null &&
			!(!piece.king && king) &&
			stepsFrom(board, move.to).some((entry) => entry.captured !== null);

		const them = otherPlayer(player);
		const scores = countPieces(board);
		const theyCanMove = allSteps(board, them).length > 0;
		const theyHavePieces = scores[them] > 0;
		const won = !theyCanMove || !theyHavePieces;

		// Nobody making progress is a draw, not an endless game.
		const sinceCapture = step.captured === null ? state.sinceCapture + 1 : 0;
		const stalled = sinceCapture >= STALL_LIMIT;

		return {
			ok: true,
			state: {
				...state,
				board,
				scores,
				sinceCapture,
				mustJumpFrom: chaining ? move.to : null,
				lastMove: { from: move.from, to: move.to, captured: step.captured },
				turn: won || stalled ? null : chaining ? player : them,
				result: won ? { winner: player } : stalled ? { winner: null } : null,
			},
		};
	},

	legalMoves(state, player) {
		if (state.result || state.turn !== player) return [];
		if (state.mustJumpFrom !== null) {
			return stepsFrom(state.board, state.mustJumpFrom)
				.filter((step) => step.captured !== null)
				.map((step) => step.move);
		}
		// When any jump exists, only jumps are on offer.
		const steps = allSteps(state.board, player);
		const jumps = steps.filter((step) => step.captured !== null);
		return (jumps.length > 0 ? jumps : steps).map((step) => step.move);
	},

	tick() {
		return null;
	},

	nextTickAt() {
		return null;
	},
};

/** Exposed for the bot and the board: what this piece can do right now. */
export function movesForPiece(state: CheckersState, square: number): CheckersMove[] {
	if (state.mustJumpFrom !== null && state.mustJumpFrom !== square) return [];
	const piece = state.board[square];
	if (!piece) return [];
	// Forced capture applies here too, or the board would offer slides that
	// `apply` then turns away.
	const mustJump =
		state.mustJumpFrom !== null ||
		allSteps(state.board, piece.owner).some((step) => step.captured !== null);
	return stepsFrom(state.board, square)
		.filter((step) => !mustJump || step.captured !== null)
		.map((step) => step.move);
}

export function captureCount(state: CheckersState, move: CheckersMove): number {
	const step = stepsFrom(state.board, move.from).find((entry) => entry.move.to === move.to);
	return step?.captured === null || step === undefined ? 0 : 1;
}
